import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Alert,
  AlertIcon,
  Badge,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tooltip,
} from '@chakra-ui/react';
import { FiCheckCircle, FiDatabase, FiMessageSquare, FiX, FiInfo, FiAlertTriangle } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { ProcessingResults } from '../services/api';

interface PostProcessingResultsProps {
  results: ProcessingResults;
  onClose: () => void;
}

const PostProcessingResults: React.FC<PostProcessingResultsProps> = ({
  results,
  onClose
}) => {
  const { document, qdrant, data_sources } = results;
  // Só é sucesso se REALMENTE indexou trechos. 0 = não conseguimos extrair texto
  // (ex.: PDF escaneado/de imagem) — não mentir dizendo "indexado".
  const indexedCount = Number(document?.chunks_count ?? qdrant?.vectors_count ?? 0) || 0;
  const indexed = indexedCount > 0;

  return (
    <Card borderColor={indexed ? 'green.500' : 'orange.400'} borderWidth={2}>
      <CardHeader bg={indexed ? 'green.50' : 'orange.50'}>
        <HStack justify="space-between">
          <HStack>
            <Icon as={indexed ? FiCheckCircle : FiAlertTriangle} color={indexed ? 'green.500' : 'orange.500'} boxSize={6} />
            <VStack align="start" spacing={0}>
              <Heading size="md" color={indexed ? 'green.700' : 'orange.700'}>
                {indexed ? 'Documento indexado' : 'Nenhum texto extraído — documento NÃO indexado'}
              </Heading>
              <Text fontSize="sm" color="gray.600">
                {indexed
                  ? 'Resumo do que ficou pronto para busca e chat'
                  : 'Não conseguimos extrair texto deste arquivo (provável PDF escaneado/de imagem).'}
              </Text>
            </VStack>
          </HStack>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
          >
            <Icon as={FiX} />
          </Button>
        </HStack>
      </CardHeader>

      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* Resumo do Processamento */}
          <Box>
            <Text fontWeight="semibold" mb={3}>Resumo do Processamento</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Card bg="blue.50">
                <CardBody>
                  <Stat>
                    <StatLabel>Documento</StatLabel>
                    <StatNumber fontSize="lg">{document.filename}</StatNumber>
                    <StatHelpText>{document.collection_name}</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
              <Card bg="purple.50">
                <CardBody>
                  <Stat>
                    <StatLabel>Trechos Indexados</StatLabel>
                    <StatNumber color="purple.500">{document.chunks_count}</StatNumber>
                    <StatHelpText>prontos para busca</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </SimpleGrid>
          </Box>

          <Divider />

          {/* Status dos Data Sources */}
          <Box>
            <Text fontWeight="semibold" mb={3}>Status da indexação</Text>
            <SimpleGrid columns={{ base: 2, md: 2 }} spacing={4}>
              <HStack>
                <Icon
                  as={data_sources?.qdrant_available ? FiCheckCircle : FiX}
                  color={data_sources?.qdrant_available ? "green.500" : "red.500"}
                />
                <Text fontSize="sm">Índice de busca</Text>
              </HStack>
              <HStack>
                <Icon
                  as={data_sources?.vectors_indexed ? FiCheckCircle : FiX}
                  color={data_sources?.vectors_indexed ? "green.500" : "red.500"}
                />
                <Text fontSize="sm">Trechos indexados</Text>
              </HStack>
            </SimpleGrid>
          </Box>

          <Divider />

          {/* Dados do Qdrant */}
          <Box>
            <HStack mb={3}>
              <Icon as={FiDatabase} />
              <Text fontWeight="semibold">Detalhes da indexação</Text>
            </HStack>
            <VStack spacing={4} align="stretch">
              {qdrant?.error ? (
                <Alert status="warning">
                  <AlertIcon />
                  <Text>Erro ao acessar o índice de busca: {qdrant.error}</Text>
                </Alert>
              ) : (
                <>
                  <Box>
                    <Text fontWeight="medium" mb={3} fontSize="sm">Informações da base</Text>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      <Stat>
                        <StatLabel>Collection</StatLabel>
                        <StatNumber fontSize="md">{qdrant?.collection_info?.name || '-'}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Total de Vetores</StatLabel>
                        <StatNumber color="blue.500">{qdrant?.vectors_count || 0}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Dimensoes</StatLabel>
                        <StatNumber fontSize="md">{qdrant?.collection_info?.config?.vector_size || '-'}</StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </Box>

                  {qdrant?.sample_vectors && qdrant.sample_vectors.length > 0 && (
                    <Box>
                      <Text fontWeight="medium" mb={3} fontSize="sm">Vetores de Exemplo (Primeiros 5)</Text>
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>ID</Th>
                            <Th>Score</Th>
                            <Th>Tem Texto</Th>
                            <Th>Metadados</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {qdrant.sample_vectors.map((vector, index) => (
                            <Tr key={index}>
                              <Td>
                                <Text fontSize="xs" fontFamily="mono">
                                  {vector.id.substring(0, 8)}...
                                </Text>
                              </Td>
                              <Td>
                                <Badge colorScheme={vector.score > 0.8 ? "green" : "orange"}>
                                  {vector.score.toFixed(3)}
                                </Badge>
                              </Td>
                              <Td>
                                <Icon
                                  as={vector.has_text ? FiCheckCircle : FiX}
                                  color={vector.has_text ? "green.500" : "red.500"}
                                />
                              </Td>
                              <Td>
                                <Tooltip label={JSON.stringify(vector.metadata, null, 2)}>
                                  <Text fontSize="xs" cursor="help" color="blue.500">
                                    {Object.keys(vector.metadata).length} campos
                                  </Text>
                                </Tooltip>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </>
              )}
            </VStack>
          </Box>

          <Divider />

          {/* Metadados */}
          <Box>
            <HStack mb={3}>
              <Icon as={FiInfo} />
              <Text fontWeight="semibold">Metadados do Documento</Text>
            </HStack>
            <VStack spacing={4} align="stretch">
              {document.statistics && Object.keys(document.statistics).length > 0 && (
                <Box>
                  <Text fontWeight="medium" mb={3} fontSize="sm">Estatisticas do Documento</Text>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    {Object.entries(document.statistics).map(([key, value]) => (
                      <Stat key={key} size="sm">
                        <StatLabel fontSize="xs">{key.replace(/_/g, ' ')}</StatLabel>
                        <StatNumber fontSize="md">
                          {typeof value === 'number' ? value.toLocaleString() : String(value)}
                        </StatNumber>
                      </Stat>
                    ))}
                  </SimpleGrid>
                </Box>
              )}

              <Box>
                <Text fontWeight="medium" mb={3} fontSize="sm">Informacoes de Processamento</Text>
                <Table size="sm">
                  <Tbody>
                    <Tr>
                      <Td fontWeight="medium">Status</Td>
                      <Td>
                        <Badge colorScheme="green">{document.status}</Badge>
                      </Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="medium">Progresso</Td>
                      <Td>{Math.round(document.progress * 100)}%</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="medium">Tempo de Processamento</Td>
                      <Td>{document.processing_time}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="medium">Timestamp</Td>
                      <Td fontSize="xs">{new Date(document.timestamp).toLocaleString()}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="medium">Collection</Td>
                      <Td>{document.collection_name}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="medium">ID do Documento</Td>
                      <Td fontSize="xs" fontFamily="mono">{document.document_id}</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Mensagem final — honesta conforme o resultado real */}
          {indexed ? (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Pronto para o chat!</Text>
                <Text fontSize="sm" mt={1}>
                  {indexedCount} trecho(s) indexado(s). Já dá para perguntar sobre este documento no Assistente.
                </Text>
              </Box>
            </Alert>
          ) : (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Este documento não foi indexado</Text>
                <Text fontSize="sm" mt={1}>
                  Não há texto reconhecível — é um <strong>PDF escaneado/de imagem</strong> (ex.: diagramas).
                  O <strong>reconhecimento de texto (OCR)</strong> é um recurso <strong>sob demanda</strong>,
                  não incluído por padrão. Ele <strong>não</strong> aparece nas buscas/chat. Para indexá-lo,
                  fale com a gente para habilitar OCR, ou envie um PDF/Word com texto selecionável.
                </Text>
              </Box>
            </Alert>
          )}

          {/* Action Buttons */}
          <HStack justify="flex-end" spacing={4}>
            <Button variant="outline" onClick={onClose}>
              Fazer Novo Upload
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={<Icon as={FiMessageSquare} />}
              as={Link}
              to="/chat"
            >
              Testar no Chat
            </Button>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default PostProcessingResults;
